import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import IconPencil from "assets/img/icon-pencil.svg?react"
import IconCheck from "assets/img/icon-check.svg?react"
import IconDocument from "assets/img/icon-document.svg?react"

import { FC, useState } from "react"
import { useTranslation } from "react-i18next"

import AssetMaintenance from "models/AssetMaintenance"
import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import MaintenanceEditModal from "components/MaintenanceEditModal/MaintenanceEditModal"
import MaintenanceCompleteModal from "components/MaintenanceCompleteModal/MaintenanceCompleteModal"

interface IAssetMaintenanceControlsProps {
    maintenance: AssetMaintenance
    onChanged: () => Promise<void>
}

const AssetMaintenanceControls: FC<IAssetMaintenanceControlsProps> = ({
    maintenance,
    onChanged,
}) => {
    const { t } = useTranslation()
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showCompleteModal, setShowCompleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const shouldShowButtons = !!(maintenance.linkDelete
        || maintenance.linkUpdate
        || maintenance.linkComplete
    )

    const { maintenanceTypesMap } = useMaintenanceTypes()
    const typeName = maintenanceTypesMap.get(maintenance.typeId)?.name

    async function handleDelete() {
        if (!maintenance.linkDelete) return

        setIsDeleting(true)

        try {
            await AssetService.deleteAssetMaintenance(maintenance.linkDelete)
            await onChanged()
            setShowDeleteConfirm(false)
        } catch(e) {
            toastMessages.showSystemError()
            setIsDeleting(false)
        }
    }

    if (maintenance.completedDate) {
        return maintenance.completedDocumentUri
            ? <Flex alignItems="center" justifyContent="flex-end">
                <Button
                    type="link"
                    theme="link"
                    url={maintenance.completedDocumentUri}
                    label={t('maintenance.showDocument')}
                    icon={<IconDocument />}
                    clamp={1}
                />
            </Flex>
            : null
    }

    if (!shouldShowButtons) return null

    return (
        <>
            <Flex gap="1.2rem" alignItems="center" justifyContent="flex-end">
                <FlexItem>
                    {!!maintenance.linkDelete &&
                        <Button
                            theme="grey"
                            round
                            icon={<IconTrashBin />}
                            ariaLabel={t("common.delete")}
                            onClick={() => setShowDeleteConfirm(true)}
                        />
                    }
                </FlexItem>
                <FlexItem>
                    {!!maintenance.linkUpdate &&
                        <Button
                            round
                            theme="grey"
                            icon={<IconPencil />}
                            ariaLabel={t("common.edit")}
                            onClick={() => setShowEditModal(true)}
                        />
                    }
                </FlexItem>
                <FlexItem>
                    {!!maintenance.linkComplete &&
                        <Button
                            round
                            icon={<IconCheck />}
                            ariaLabel={t("maintenance.completeMaintenance")}
                            onClick={() => setShowCompleteModal(true)}
                        />
                    }
                </FlexItem>
            </Flex>
            {showDeleteConfirm &&
                <ConfirmModal
                    title={t('common.warning')}
                    theme="danger"
                    maxWidth="586px"
                    isBusy={isDeleting}
                    confirmButtonLabel={t('common.delete')}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDelete}>
                    {t('maintenance.deleteConfirm')}
                    <div className="mt-2 font-bold">{typeName}</div>
                </ConfirmModal>
            }
            {showEditModal &&
                <MaintenanceEditModal
                    maintenance={maintenance}
                    onClose={() => setShowEditModal(false)}
                    onChanged={onChanged}
                />
            }
            {showCompleteModal &&
                <MaintenanceCompleteModal
                    maintenance={maintenance}
                    onClose={() => setShowCompleteModal(false)}
                    onCompleted={onChanged}
                />
            }
        </>
    )
}

export default AssetMaintenanceControls
